/**
 * Stripe Integration Utilities
 *
 * This module provides utilities for integrating with Stripe for payment processing.
 * It includes functions for loading Stripe, creating payment intents, and handling
 * payment method collection.
 */

import { Stripe, StripeElements, loadStripe } from "@stripe/stripe-js";

// Stripe publishable key - should be set in environment variables
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

let stripePromise: Promise<Stripe | null>;

/**
 * Get Stripe instance
 */
export const getStripe = () => {
	if (!stripePromise) {
		if (!STRIPE_PUBLISHABLE_KEY) {
			console.error(
				"Stripe publishable key is not set in environment variables",
			);
			return Promise.resolve(null);
		}
		stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
	}
	return stripePromise;
};

/**
 * Create a setup intent for saving payment methods
 */
export const createSetupIntent = async (): Promise<{
	clientSecret: string;
} | null> => {
	try {
		const response = await fetch("/api/stripe/create-setup-intent", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error("Failed to create setup intent");
		}

		const data = await response.json();
		return data;
	} catch (error) {
		console.error("Error creating setup intent:", error);
		return null;
	}
};

/**
 * Confirm a payment intent
 */
export const confirmPayment = async (
	stripe: Stripe,
	elements: StripeElements,
	clientSecret: string,
	returnUrl: string,
) => {
	try {
		const { error, paymentIntent } = await stripe.confirmPayment({
			elements,
			confirmParams: {
				return_url: returnUrl,
			},
		});

		if (error) {
			console.error("Payment confirmation error:", error);
			return { error, paymentIntent: null };
		}

		return { error: null, paymentIntent };
	} catch (error) {
		console.error("Error confirming payment:", error);
		return { error, paymentIntent: null };
	}
};

/**
 * Create a payment method
 */
export const createPaymentMethod = async (
	stripe: Stripe,
	elements: StripeElements,
	billingDetails?: {
		name?: string;
		email?: string;
		address?: {
			line1: string;
			line2?: string;
			city: string;
			state: string;
			postal_code: string;
			country: string;
		};
	},
) => {
	try {
		const cardElement = elements.getElement("card");
		if (!cardElement) {
			throw new Error("Card element not found");
		}

		const { error, paymentMethod } = await stripe.createPaymentMethod({
			type: "card",
			card: cardElement,
			billing_details: billingDetails,
		});

		if (error) {
			console.error("Payment method creation error:", error);
			return { error, paymentMethod: null };
		}

		return { error: null, paymentMethod };
	} catch (error) {
		console.error("Error creating payment method:", error);
		return { error, paymentMethod: null };
	}
};

/**
 * Format currency amount for display
 */
export const formatCurrency = (
	amount: number,
	currency: string = "usd",
): string => {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency.toUpperCase(),
	}).format(amount / 100);
};

/**
 * Get card brand icon
 */
export const getCardBrandIcon = (brand: string): string => {
	const brandIcons: Record<string, string> = {
		visa: "💳",
		mastercard: "💳",
		amex: "💳",
		discover: "💳",
		diners: "💳",
		jcb: "💳",
		unionpay: "💳",
		unknown: "💳",
	};

	return brandIcons[brand.toLowerCase()] || brandIcons.unknown;
};

/**
 * Validate Stripe environment
 */
export const validateStripeEnvironment = (): boolean => {
	if (!STRIPE_PUBLISHABLE_KEY) {
		console.error(
			"Stripe publishable key is missing. Please set VITE_STRIPE_PUBLISHABLE_KEY in your environment variables.",
		);
		return false;
	}

	if (STRIPE_PUBLISHABLE_KEY.startsWith("pk_test_")) {
		console.warn(
			"Using Stripe test mode. Make sure to use live keys in production.",
		);
	}

	return true;
};

/**
 * Stripe error handler
 */
export const handleStripeError = (error: any): string => {
	if (error.type === "card_error" || error.type === "validation_error") {
		return error.message;
	}

	if (error.type === "rate_limit_error") {
		return "Too many requests made to the API too quickly. Please try again later.";
	}

	if (error.type === "invalid_request_error") {
		return "Invalid parameters were supplied to Stripe API.";
	}

	if (error.type === "api_error") {
		return "An error occurred with Stripe API. Please try again later.";
	}

	if (error.type === "api_connection_error") {
		return "Network communication with Stripe failed. Please check your connection.";
	}

	if (error.type === "authentication_error") {
		return "Authentication with Stripe API failed.";
	}

	return "An unexpected error occurred. Please try again.";
};
